import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { localizeApiError } from "@/i18n/apiErrors";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Auto-redirect to login after 3 seconds on success
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate("/"), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError(t("errResetLinkInvalid"));
      return;
    }
    if (password.length < 8) {
      setError(t("errPasswordMinLength"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("errPasswordsDontMatch"));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(localizeApiError(data.detail, t));
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError(t("errNetwork"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] text-[#3D4035] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <h1
            className="text-3xl md:text-4xl mb-3 text-[#3D4035]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}
          >
            {t("resetInvalidTitle")}
          </h1>
          <p className="text-[#3D4035]/70 font-body mb-8">
            {t("resetInvalidBody")}
          </p>
          <Link
            to="/forgot-password"
            className="inline-block bg-[#7B9E7F] text-white px-8 py-3 rounded-full font-body font-bold hover:opacity-90 transition-all active:scale-[0.98] shadow-lg shadow-[#7B9E7F]/20"
          >
            {t("resetRequestNew")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#3D4035] flex flex-col">
      <header className="border-b border-[#E8E5E0]">
        <div className="max-w-md mx-auto px-6 py-6 flex items-center">
          <Link to="/" className="text-[#7B9E7F] font-body font-medium hover:opacity-80 transition">
            ← MealPill
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {!success ? (
            <>
              <h1
                className="text-3xl md:text-4xl mb-3 text-[#3D4035]"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}
              >
                {t("resetSetNewTitle")}
              </h1>
              <p className="text-[#3D4035]/70 font-body mb-8 leading-relaxed">
                {t("resetSetNewDesc")}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A5A58D]" strokeWidth={1.5} />
                  <input
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("resetNewPasswordPh")}
                    className="w-full bg-white border-transparent ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-14 pl-12 pr-4 font-body text-[#3D4035] placeholder:text-[#A5A58D] outline-none transition-all"
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A5A58D]" strokeWidth={1.5} />
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t("resetConfirmPh")}
                    className="w-full bg-white border-transparent ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-14 pl-12 pr-4 font-body text-[#3D4035] placeholder:text-[#A5A58D] outline-none transition-all"
                  />
                </div>

                {error && (
                  <div className="text-[#BC4749] text-sm font-body bg-[#BC4749]/10 rounded-xl px-4 py-3">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full bg-[#7B9E7F] text-white rounded-full h-14 font-body font-bold text-lg hover:opacity-90 transition-all active:scale-[0.98] shadow-lg shadow-[#7B9E7F]/20 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {t("resetSubmit")}
                      <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#7B9E7F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-[#7B9E7F]" strokeWidth={1.5} />
              </div>
              <h1
                className="text-3xl md:text-4xl mb-3 text-[#3D4035]"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}
              >
                {t("resetDoneTitle")}
              </h1>
              <p className="text-[#3D4035]/70 font-body leading-relaxed mb-8">
                {t("resetDoneBody")}<br />
                {t("resetRedirecting")}
              </p>
              <Link
                to="/"
                className="inline-block bg-[#7B9E7F] text-white px-8 py-3 rounded-full font-body font-bold hover:opacity-90 transition-all active:scale-[0.98] shadow-lg shadow-[#7B9E7F]/20"
              >
                {t("resetGoToSignIn")}
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
